import React, { useEffect } from 'react';
import { ClinicSettings } from '../types';

// --- PROPS INTERFACE ---
interface CaseReportPDFPrestigeProps {
    reportData: any;
    originalImageUrl: string;
    generatedImageUrl: string;
    smileRating: any;
    afterSmileRating: any;
    patientPortraitUrl?: string | null;
    clinicSettings?: ClinicSettings;
    onRendered: () => void;
}

// --- EMBEDDED ICONS for Prestige Theme ---
const PrestigeFuturaSmileIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3L13.06 11.06L21 12L13.06 12.94L12 21L10.94 12.94L3 12L10.94 11.06L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
);

const PrestigeCheckmarkIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" xmlns="http://www.w3.org/2000/svg" >
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

// --- CORE LAYOUT COMPONENTS ---
const Page: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
    <div
        className={`pdf-page-prestige relative w-[210mm] h-[297mm] bg-[#F9F9F7] text-[#333333] flex flex-col font-sans overflow-hidden ${className}`}
        style={{ fontFamily: "'Inter', sans-serif", pageBreakAfter: 'always' }}
    >
        {children}
    </div>
);

const PageHeader: React.FC<{ clinicName?: string }> = ({ clinicName }) => (
    <header className="w-full flex justify-between items-center pb-2 border-b border-[#333333]/20 flex-shrink-0 px-[15mm] pt-[15mm]">
        <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#333333] flex items-center justify-center">
                <PrestigeFuturaSmileIcon className="w-4 h-4 text-[#F9F9F7]" />
            </div>
            <span className="text-sm font-bold text-[#333333] tracking-[0.2em]">{clinicName || 'FUTURASMILE'}</span>
        </div>
        <span className="text-[10px] font-medium text-[#333333]/50 tracking-widest">PRESTIGE REPORT</span>
    </header>
);

const PageFooter: React.FC<{ pageNumber: number; totalPages: number; clinicName?: string }> = ({ pageNumber, totalPages, clinicName }) => (
    <footer className="w-full mt-auto pt-2 border-t border-[#333333]/20 flex justify-between items-center text-[9px] text-[#333333]/50 font-medium flex-shrink-0 px-[15mm] pb-[15mm]">
        <p>{clinicName || 'FuturaSmile'} | AI-Powered Smile Simulation Report</p>
        <p>Page {pageNumber} of {totalPages}</p>
    </footer>
);

const SectionHeader: React.FC<{ title: string, subtitle: string }> = ({ title, subtitle }) => (
    <div className="text-center my-6">
        <p className="text-xs font-semibold tracking-[0.3em] text-[#BFA588]">{subtitle}</p>
        <h2 className="text-4xl text-[#333333] mt-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>{title}</h2>
    </div>
);

const ComparisonBar: React.FC<{ label: string; beforeScore: number; afterScore: number }> = ({ label, beforeScore, afterScore }) => {
    const goldColor = '#BFA588';
    return (
        <div className="w-full">
            <div className="flex justify-between items-end mb-1.5">
                <p className="font-semibold text-sm text-zinc-700">{label}</p>
                <p className="text-xs font-medium">
                    <span className="text-zinc-500">{beforeScore}</span>
                    <span className="text-zinc-400 mx-1">&rarr;</span>
                    <span className="font-bold" style={{ color: goldColor }}>{afterScore}</span>
                </p>
            </div>
            <div className="relative h-2.5 w-full bg-zinc-200 rounded-full overflow-hidden">
                <div className="absolute top-0 left-0 h-full bg-zinc-400 rounded-full" style={{ width: `${beforeScore}%` }}></div>
                <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${afterScore}%`, backgroundColor: goldColor }}></div>
            </div>
        </div>
    );
};

// --- RADAR CHART COMPONENT for Prestige Theme---
const PrestigeRadarChart: React.FC<{ beforeData: any, afterData: any }> = ({ beforeData, afterData }) => {
    // Guard against empty or invalid data to prevent NaN polygon errors
    if (!beforeData?.length || !afterData?.length || beforeData.length < 3) {
        return null;
    }

    const size = 300;
    const center = size / 2;
    const radius = center * 0.7;
    const numAxes = beforeData.length;
    const angleSlice = (Math.PI * 2) / numAxes;
    const levels = 4;
    const goldColor = '#BFA588';
    const darkColor = '#333333';

    // Grid
    const gridLevels = Array.from({ length: levels }, (_, i) => {
        const gridRadius = radius * ((i + 1) / levels);
        const points = beforeData.map((_: any, j: number) => {
            const angle = angleSlice * j - Math.PI / 2;
            const x = center + gridRadius * Math.cos(angle);
            const y = center + gridRadius * Math.sin(angle);
            return `${x},${y}`;
        }).join(' ');
        return <polygon key={i} points={points} fill="none" stroke={darkColor} strokeOpacity="0.1" strokeWidth="1" />;
    });

    // Axes
    const axesLines = beforeData.map((_: any, i: number) => {
        const angle = angleSlice * i - Math.PI / 2;
        const x = center + radius * Math.cos(angle);
        const y = center + radius * Math.sin(angle);
        return <line key={i} x1={center} y1={center} x2={x} y2={y} stroke={darkColor} strokeOpacity="0.1" strokeWidth="1" />;
    });

    // Labels
    const axesLabels = beforeData.map((item: any, i: number) => {
        const angle = angleSlice * i - Math.PI / 2;
        const labelRadius = radius * 1.25;
        const x = center + labelRadius * Math.cos(angle);
        const y = center + labelRadius * Math.sin(angle);
        const parts = item.label.split('\n');
        return (
            <text key={i} x={x} y={y} fill={darkColor} fillOpacity="0.7" fontSize="10" fontWeight="600" textAnchor="middle" dominantBaseline="middle">
                {parts.map((part: string, index: number) => (
                    <tspan key={index} x={x} dy={index === 0 ? 0 : '1.2em'}>{part}</tspan>
                ))}
            </text>
        );
    });

    // Data polygons
    const beforePoints = beforeData.map((item: any, i: number) => {
        const pointRadius = radius * ((item.score ?? 0) / 100);
        const angle = angleSlice * i - Math.PI / 2;
        const x = center + pointRadius * Math.cos(angle);
        const y = center + pointRadius * Math.sin(angle);
        return `${x},${y}`;
    }).join(' ');

    const afterPoints = afterData.map((item: any, i: number) => {
        const pointRadius = radius * ((item.score ?? 0) / 100);
        const angle = angleSlice * i - Math.PI / 2;
        const x = center + pointRadius * Math.cos(angle);
        const y = center + pointRadius * Math.sin(angle);
        return `${x},${y}`;
    }).join(' ');

    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <g>
                {gridLevels}
                {axesLines}
                {axesLabels}
                <polygon points={beforePoints} fill={darkColor} fillOpacity="0.2" stroke={darkColor} strokeOpacity="0.5" strokeWidth="2" />
                <polygon points={afterPoints} fill={goldColor} fillOpacity="0.25" stroke={goldColor} strokeWidth="2" />
            </g>
        </svg>
    );
};

const RatingScaleGuide: React.FC = () => {
    const scale = [
        { range: '85 - 100', title: 'Exceptional', description: 'Represents a clinically ideal smile that is in perfect harmony with facial features. A benchmark for aesthetic excellence.', color: '#BFA588' },
        { range: '70 - 84', title: 'Very Good', description: 'An attractive and healthy smile with only minor deviations from the ideal. Excellent foundation.', color: '#707070' },
        { range: '55 - 69', title: 'Good', description: 'A pleasant smile with noticeable areas that could be enhanced for better symmetry, color, or proportion.', color: '#909090' },
        { range: '40 - 54', title: 'Fair', description: 'The smile has several areas with significant potential for aesthetic improvement.', color: '#B0B0B0' },
        { range: 'Below 40', title: 'Needs Attention', description: 'Indicates foundational opportunities for enhancement to improve overall dental aesthetics.', color: '#C0C0C0' }
    ];

    return (
        <div className="space-y-4 w-full max-w-2xl mx-auto">
            {scale.map(item => (
                <div key={item.range} className="flex items-start gap-5 p-3 border-b border-black/5 last:border-b-0">
                    <div className="w-28 flex-shrink-0 text-right">
                        <p className="text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: item.color }}>{item.range}</p>
                    </div>
                    <div className="flex-1">
                        <p className="font-bold text-base" style={{ color: item.color }}>{item.title}</p>
                        <p className="text-xs text-zinc-600 leading-snug">{item.description}</p>
                    </div>
                </div>
            ))}
        </div>
    );
};


// --- MAIN PDF COMPONENT ---
const CaseReportPDFPrestige: React.FC<CaseReportPDFPrestigeProps> = ({ reportData, originalImageUrl, generatedImageUrl, smileRating, afterSmileRating, patientPortraitUrl, clinicSettings, onRendered }) => {
    useEffect(() => {
        const timer = setTimeout(() => onRendered(), 200);
        return () => clearTimeout(timer);
    }, [onRendered]);

    if (!reportData || !smileRating || !afterSmileRating) {
        return (
            <Page>
                <p>Loading Prestige report data...</p>
            </Page>
        );
    }

    const { reportTitle, afterVision, transformationAnalysis, treatmentPath, nextSteps } = reportData;
    const scoreBefore = smileRating?.overallSmileScore?.score ?? 0;
    const scoreAfter = afterSmileRating?.overallSmileScore?.score ?? 0;

    const hasPortraitPage = !!patientPortraitUrl;
    const totalPages = 6 + (hasPortraitPage ? 1 : 0);
    const coverTitle = reportTitle.split(': ')[1] || 'A Vision of Your Best Smile';

    const ScoreCard: React.FC<{ title: string; score: number; color: string; }> = ({ title, score, color }) => (
        <div className="text-center w-40">
            <p className="text-xs font-semibold tracking-[0.2em] text-zinc-500">{title}</p>
            <p className="text-6xl flex items-baseline justify-center" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: color }}>
                {score}
                <span className="text-2xl text-zinc-400/80 font-sans font-medium ml-1">/100</span>
            </p>
        </div>
    );

    const radarChartData = [
        { label: 'Symmetry', score: smileRating?.detailedScores?.symmetryAndAlignment ?? 0 },
        { label: 'Brightness', score: smileRating?.detailedScores?.colorAndBrightness ?? 0 },
        { label: 'Proportions', score: smileRating?.detailedScores?.shapeAndProportions ?? 0 },
        { label: 'Gum Line', score: smileRating?.detailedScores?.gumHealthAndContour ?? 0 },
    ];
    const radarChartDataAfter = [
        { label: 'Symmetry', score: afterSmileRating?.detailedScores?.symmetryAndAlignment ?? 0 },
        { label: 'Brightness', score: afterSmileRating?.detailedScores?.colorAndBrightness ?? 0 },
        { label: 'Proportions', score: afterSmileRating?.detailedScores?.shapeAndProportions ?? 0 },
        { label: 'Gum Line', score: afterSmileRating?.detailedScores?.gumHealthAndContour ?? 0 },
    ];

    return (
        <div style={{ background: '#F9F9F7', fontFamily: "'Inter', sans-serif" }}>
            {/* Page 1: Cover Page */}
            <Page className="p-0">
                {/* Background Image Div */}
                <div className="absolute inset-0 w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${generatedImageUrl})` }} />
                {/* Vignette Overlay */}
                <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(28,28,28,0.1) 20%, rgba(28,28,28,0.95) 90%)' }} />

                {/* Content Container */}
                <div className="relative w-full h-full flex flex-col justify-between items-center text-center text-[#F8F5F2] p-[15mm]">
                    {/* Header Branding */}
                    <div className="w-full flex flex-col items-center opacity-80">
                        <div className="w-8 h-8 rounded-full border border-white/50 flex items-center justify-center">
                            <PrestigeFuturaSmileIcon className="w-4 h-4 text-white/80" />
                        </div>
                        <p className="mt-2 text-xs font-bold tracking-[0.3em] text-white/80">FUTURASMILE</p>
                    </div>

                    {/* Main Title Block */}
                    <div className="flex flex-col items-center -mt-20">
                        <p className="font-semibold tracking-[0.3em] text-[#BFA588]">PRESTIGE REPORT</p>
                        <h1 className="text-6xl mt-2 text-white max-w-lg" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}>
                            {coverTitle}
                        </h1>
                        <p className="text-sm text-white/70 mt-4 max-w-md leading-relaxed">
                            {reportData.introduction?.body}
                        </p>
                    </div>

                    {/* Footer */}
                    <div className="w-full text-center">
                        <p className="text-[9px] text-white/40 font-medium">
                            Report Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                    </div>
                </div>
            </Page>

            {/* Page 2: Your Vision Realized */}
            <Page>
                <PageHeader />
                <main className="flex-1 flex flex-col justify-center px-[15mm]">
                    <SectionHeader subtitle="THE AFTER VISION" title="Your Smile, Perfected" />
                    <div className="flex-1 flex items-center gap-10">
                        <div className="w-1/2 flex flex-col justify-center">
                            <p className="text-sm text-[#333333]/70 max-w-2xl leading-relaxed mb-6">{afterVision.summary}</p>
                            <div className="space-y-4">
                                {afterVision.keyChanges.map((item: string, index: number) => (
                                    <div key={index} className="flex items-start gap-3">
                                        <div className="w-6 h-6 rounded-full border-2 border-[#BFA588] flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <PrestigeCheckmarkIcon className="w-3 h-3 text-[#BFA588]" />
                                        </div>
                                        <p className="text-[#333333] text-sm font-semibold leading-snug">{item}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8 p-4 bg-white rounded-lg shadow-lg border border-black/5 flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-semibold tracking-widest text-zinc-500">PROJECTED SCORE</p>
                                    <p className="text-5xl font-bold mt-1" style={{ color: '#BFA588' }}>{scoreAfter}<span className="text-3xl text-zinc-400">/100</span></p>
                                </div>
                                <div className="w-px h-12 bg-zinc-200" />
                                <div>
                                    <p className="text-xs font-semibold tracking-widest text-zinc-500">IMPROVEMENT</p>
                                    <p className="text-5xl font-bold text-zinc-700">+{transformationAnalysis?.overallImprovement?.scoreChange ?? 0} <span className="text-3xl text-zinc-400">pts</span></p>
                                </div>
                            </div>
                        </div>
                        <div className="w-1/2 h-[70%] flex items-center justify-center">
                            <div className="w-full h-full rounded-lg overflow-hidden p-1.5 bg-zinc-100 shadow-xl border border-black/10 flex items-center justify-center">
                                <img src={generatedImageUrl} className="max-w-full max-h-full object-contain rounded-md" alt="Generated Smile Vision" />
                            </div>
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={2} totalPages={totalPages} />
            </Page>

            {/* Page 3: Transformation Analysis */}
            <Page>
                <PageHeader />
                <main className="flex-1 flex flex-col justify-start items-center px-[15mm] py-4">
                    <SectionHeader subtitle="TRANSFORMATION ANALYSIS" title="Aesthetic Score Improvement" />
                    <p className="text-center text-sm text-[#333333]/70 max-w-3xl leading-relaxed mb-6 -mt-2">{transformationAnalysis?.overallImprovement?.summary ?? ''}</p>

                    <div className="w-full max-w-4xl flex items-center justify-around my-4 p-6 bg-gradient-to-br from-white to-zinc-100 rounded-lg shadow-2xl border border-black/10">
                        <ScoreCard title="INITIAL SCORE" score={scoreBefore} color="#333333" />
                        <div className="text-center">
                            <svg className="w-12 h-12 text-[#BFA588]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                            </svg>
                            <p className="font-bold text-2xl mt-1" style={{ color: '#BFA588' }}>
                                +{transformationAnalysis?.overallImprovement?.scoreChange ?? 0}
                            </p>
                            <p className="text-xs font-medium text-zinc-500">Points Gained</p>
                        </div>
                        <ScoreCard title="PROJECTED SCORE" score={scoreAfter} color="#BFA588" />
                    </div>

                    <div className="w-full max-w-3xl flex flex-col items-center gap-10 mt-6">
                        <div className="w-full flex flex-col items-center">
                            <h3 className="text-sm font-bold tracking-[0.2em] text-zinc-500 mb-4">METRIC BREAKDOWN</h3>
                            <div className="w-full space-y-5">
                                {(transformationAnalysis?.comparativeMetrics || []).map((metric: any) => (
                                    <ComparisonBar key={metric.area} label={metric.area} beforeScore={metric.beforeScore} afterScore={metric.afterScore} />
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col items-center">
                            <h3 className="text-sm font-bold tracking-[0.2em] text-zinc-500 mb-2">SCORE DISTRIBUTION</h3>
                            <PrestigeRadarChart beforeData={radarChartData} afterData={radarChartDataAfter} />
                            <div className="flex gap-4 items-center justify-center mt-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#333333', opacity: 0.5 }}></div>
                                    <span className="text-xs text-zinc-600 font-medium">Initial Smile</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#BFA588' }}></div>
                                    <span className="text-xs font-medium" style={{ color: '#BFA588' }}>Projected Smile</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={3} totalPages={totalPages} />
            </Page>

            {/* Page 4: Visual Comparison */}
            <Page>
                <PageHeader />
                <main className="flex-1 flex flex-col items-center pt-4 min-h-0 px-[15mm] pb-[15mm]">
                    <SectionHeader subtitle="VISUAL EVIDENCE" title="Side-by-Side Comparison" />
                    <div className="flex-1 flex items-stretch justify-center w-full gap-5">
                        <div className="w-1/2 flex flex-col items-center">
                            <p className="font-bold text-base text-zinc-500 mb-2 tracking-[0.2em]">BEFORE</p>
                            <div className="flex-1 w-full rounded-lg overflow-hidden border-2 border-zinc-300 bg-zinc-200 flex items-center justify-center">
                                <img src={originalImageUrl} className="max-w-full max-h-full object-contain" alt="Original Smile" />
                            </div>
                        </div>
                        <div className="w-1/2 flex flex-col items-center">
                            <p className="font-bold text-base mb-2 tracking-[0.2em]" style={{ color: '#BFA588' }}>AFTER</p>
                            <div className="flex-1 w-full rounded-lg overflow-hidden border-2 bg-zinc-200 flex items-center justify-center" style={{ borderColor: '#BFA588' }}>
                                <img src={generatedImageUrl} className="max-w-full max-h-full object-contain" alt="Generated Smile Vision" />
                            </div>
                        </div>
                    </div>
                </main>
                <PageFooter pageNumber={4} totalPages={totalPages} />
            </Page>

            {/* Page 5: Rating Scale Guide */}
            <Page>
                <PageHeader />
                <main className="flex-1 flex flex-col justify-center items-center px-[15mm]">
                    <SectionHeader subtitle="AESTHETIC RATING SCALE" title="Understanding Your Score" />
                    <RatingScaleGuide />
                </main>
                <PageFooter pageNumber={5} totalPages={totalPages} />
            </Page>

            {/* Page 6: Patient Portrait (Conditional) */}
            {hasPortraitPage && patientPortraitUrl && (
                <Page>
                    <PageHeader />
                    <main className="flex-1 flex flex-col justify-center items-center px-[15mm]">
                        <div className="w-full bg-[#FFFCF7] p-4 rounded-lg border border-[#BFA588]/50" style={{ boxShadow: '0 10px 30px -10px rgba(191, 165, 136, 0.2)' }}>
                            <h1 className="text-center text-3xl mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#BFA588' }}>
                                Your Showcase Portrait
                            </h1>
                            <div className="w-full aspect-[3/2] rounded-md overflow-hidden bg-zinc-200 shadow-inner">
                                <img src={patientPortraitUrl} className="w-full h-full object-cover" alt="Patient Showcase Portrait" />
                            </div>
                            <div className="mt-5 text-center">
                                <p className="text-2xl italic text-zinc-600" style={{ fontFamily: "'Playfair Display', serif" }}>
                                    "The reflection of your true self, revealed."
                                </p>
                            </div>
                        </div>
                    </main>
                    <footer className="w-full mt-auto pt-2 border-t border-[#333333]/20 flex justify-between items-center text-[8px] text-[#333333]/80 font-medium flex-shrink-0 px-[15mm] pb-[15mm]">
                        <p>This Showcase Portrait is an artistic, AI-generated representation of your potential outcome.</p>
                        <p>Page {6} of {totalPages}</p>
                    </footer>
                </Page>
            )}

            {/* Page 7 (or 6): Next Steps */}
            <Page>
                <PageHeader />
                <main className="flex-1 flex flex-col justify-center items-center px-[15mm]">
                    <SectionHeader subtitle="YOUR JOURNEY FORWARD" title="The Path to Your New Smile" />
                    <div className="w-full max-w-2xl text-left p-8 bg-white rounded-lg shadow-xl space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-zinc-800" style={{ fontFamily: "'Playfair Display', serif" }}>{treatmentPath.headline}</h3>
                            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{treatmentPath.body}</p>
                        </div>
                        <div className="w-full h-px bg-zinc-200" />
                        <div>
                            <h3 className="text-lg font-semibold text-zinc-800" style={{ fontFamily: "'Playfair Display', serif" }}>{nextSteps.headline}</h3>
                            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{nextSteps.body}</p>
                        </div>
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-6 max-w-2xl text-center leading-relaxed">{nextSteps.disclaimer}</p>
                </main>
                <PageFooter pageNumber={hasPortraitPage ? 7 : 6} totalPages={totalPages} />
            </Page>
        </div>
    );
};

export default CaseReportPDFPrestige;